import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Actions,
  AppArea,
  SortingDirection,
  StoreFilterInput,
  StoresDocument,
  StoreSortingInput,
  StoresQuery,
  Subjects,
} from 'src/graphql/generated/graphql';
import { Routes } from 'src/components/common/router/constants';
import { queryStringToObject } from 'src/utils/query-string';
import { RestaurantsFilter } from './filter';
import { RestaurantsTable } from './table';
import { RestaurantsMap } from './map';
import { ViewSwitcher, ViewType } from './view-switcher';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { Pagination } from 'src/components/common/list/pagination';
import { Can } from 'src/utils/can';

const PAGINATION_LIMIT = 20;

export const ListRestaurants = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultFilter = queryStringToObject(searchParams.toString())?.filter;
  const defaultView = queryStringToObject(searchParams.toString())?.view;

  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<StoreFilterInput>(defaultFilter);
  const [sorting, setSorting] = useState<StoreSortingInput>({
    name: SortingDirection.Asc,
  });
  const [viewType, setViewType] = useState(defaultView || ViewType.Table);
  const [totalCount, setTotalCount] = useState(0);
  const [sortingField, sortingDirection] = Object.entries(sorting)?.[0];

  const { loading, data } = useQuery<StoresQuery>(StoresDocument, {
    variables: {
      filter,
      sorting,
      pagination: {
        limit: PAGINATION_LIMIT,
        offset: page * PAGINATION_LIMIT,
      },
      includeAttentionNeeded: true,
      includeUsersData: true,
    },
    onCompleted: (data) => setTotalCount(data?.stores?.pagination?.totalCount),
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });
  const stores = data?.stores?.records || [];

  const onFilterChange = (
    filter: StoreFilterInput & { storeNumber?: string },
  ) => {
    setPage(0);
    const { storeNumber, ...rest } = filter;
    const newFilter = {
      storeNumbers: storeNumber ? [storeNumber] : undefined,
      ...rest,
    };
    setFilter(newFilter);
  };

  const onColumnSortChange = (
    changedColumn: string,
    direction: 'desc' | 'asc',
  ) => {
    const newSorting = {
      [changedColumn]:
        direction === 'desc' ? SortingDirection.Desc : SortingDirection.Asc,
    };
    setSorting(newSorting);
  };

  const onRowClick = (_: any, { rowIndex }: { rowIndex: number }) => {
    const store = stores[rowIndex];
    navigate(`${Routes.restaurants.path}/${store.id}`);
  };

  return (
    <Can I={Actions.Read} a={Subjects.Stores} appArea={AppArea.Common}>
      <FilterWrapper>
        <RestaurantsFilter
          onSubmit={onFilterChange}
          defaultFilter={defaultFilter}
        />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          <ViewSwitcher onChange={setViewType} selectedViewType={viewType} />
          {viewType === ViewType.Map && (
            <>
              <Pagination
                onPageChange={setPage}
                count={totalCount || 0}
                rowsPerPage={PAGINATION_LIMIT}
                page={page}
              />
              <RestaurantsMap data={stores as any} />
            </>
          )}
          {viewType === ViewType.Table && (
            <RestaurantsTable
              data={stores}
              className="mt-1"
              loading={loading}
              options={{
                page,
                count: totalCount,
                sort: true,
                onColumnSortChange,
                rowsPerPage: PAGINATION_LIMIT,
                onRowClick,
                onChangePage: setPage,
                sortOrder: {
                  name: sortingField,
                  direction:
                    sortingDirection === SortingDirection.Desc ? 'desc' : 'asc',
                },
                enableNestedDataAccess: '.',
              }}
            />
          )}
        </TableWrapper>
      </ContentWrapper>
    </Can>
  );
};
